import React, { useRef, useState } from "react";
import imageCompression from "browser-image-compression";
import { X, Loader2, Plus } from "lucide-react";
import { useTranslation } from "react-i18next";
import { api } from "../../lib/api";
import { RemoteImage } from "../../core/components/RemoteImage";

interface MultiImageUploadProps {
    value?: string[];
    onChange: (urls: string[]) => void;
    label?: string;
    className?: string;
    maxSizeMB?: number;
    maxFiles?: number;
}

export const MultiImageUpload: React.FC<MultiImageUploadProps> = ({
    value = [],
    onChange,
    label,
    className = "",
    maxSizeMB = 1,
    maxFiles = 10,
}) => {
    const { t } = useTranslation();
    const fileInputRef = useRef<HTMLInputElement>(null);
    const [uploading, setUploading] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const handleFileChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
        const files = event.target.files;
        if (!files || files.length === 0) return;

        if (value.length + files.length > maxFiles) {
            setError(`Max ${maxFiles} images allowed`);
            return;
        }

        setUploading(true);
        setError(null);

        const newUrls: string[] = [];

        try {
            for (let i = 0; i < files.length; i++) {
                const file = files[i];

                // 1. Compress and convert to WebP
                const options = {
                    maxSizeMB,
                    maxWidthOrHeight: 1920,
                    useWebWorker: true,
                    fileType: "image/webp",
                };

                const compressedFile = await imageCompression(file, options);
                const webpFile = new File([compressedFile], file.name.replace(/\.[^/.]+$/, "") + ".webp", {
                    type: "image/webp",
                });

                // 2. Upload to backend
                const formData = new FormData();
                formData.append("file", webpFile);

                const response = await api.post<{ success: boolean; url: string }>("/api/upload", formData, {
                    headers: { "Content-Type": "multipart/form-data" },
                });

                if (response.data.success) {
                    newUrls.push(response.data.url);
                } else {
                    console.error("Upload failed for file:", file.name);
                }
            }

            onChange([...value, ...newUrls]);
        } catch (err: any) {
            console.error("Upload error:", err);
            setError(err.message || t("uploadFailed", "Upload failed"));
        } finally {
            setUploading(false);
            if (fileInputRef.current) fileInputRef.current.value = "";
        }
    };

    const handleRemove = (index: number) => {
        const newValue = [...value];
        newValue.splice(index, 1);
        onChange(newValue);
    };

    return (
        <div className={`space-y-3 ${className}`}>
            {label && <label className="block text-sm font-medium text-gray-700">{label}</label>}

            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                {/* Existing Images */}
                {value.map((url, index) => (
                    <div key={index} className="relative group aspect-square border rounded-lg overflow-hidden bg-gray-50">
                        <RemoteImage
                            src={url}
                            alt={`Gallery ${index + 1}`}
                            className="w-full h-full object-cover"
                        />
                        <button
                            type="button"
                            onClick={() => handleRemove(index)}
                            className="absolute top-1 right-1 p-1 bg-red-500 text-white rounded-full opacity-0 group-hover:opacity-100 transition-opacity"
                        >
                            <X className="w-3 h-3" />
                        </button>
                    </div>
                ))}

                {/* Upload Button */}
                {value.length < maxFiles && (
                    <div
                        onClick={() => !uploading && fileInputRef.current?.click()}
                        className={`
                            relative aspect-square border-2 border-dashed rounded-lg flex flex-col items-center justify-center cursor-pointer transition-colors
                            ${error ? "border-red-300 bg-red-50" : "border-gray-300 hover:border-secondary hover:bg-gray-50"}
                        `}
                    >
                        {uploading ? (
                            <Loader2 className="w-6 h-6 animate-spin text-secondary" />
                        ) : (
                            <>
                                <Plus className="w-6 h-6 text-gray-400 mb-1" />
                                <span className="text-xs text-gray-500">Add Image</span>
                            </>
                        )}
                        <input
                            type="file"
                            ref={fileInputRef}
                            onChange={handleFileChange}
                            accept="image/*"
                            multiple
                            className="hidden"
                            disabled={uploading}
                        />
                    </div>
                )}
            </div>

            {error && <p className="text-xs text-red-500 mt-1">{error}</p>}
            <p className="text-xs text-gray-400">
                Max {maxFiles} images. {maxSizeMB}MB each.
            </p>
        </div>
    );
};
